import { Link } from "react-router-dom";
import { TrendingUp, HeartHandshake, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { AgesLogo } from "@/components/AgesLogo";
import { Seo } from "@/components/seo/Seo";
import agesHero from "@/assets/ages-hero-1024.webp";
import agesHero400 from "@/assets/ages-hero-400.webp";
import agesHero800 from "@/assets/ages-hero-800.webp";
import landingBg from "@/assets/landing-bg.webp";
import { StoriesCarousel } from "@/components/landing/StoriesCarousel";
import { CoachesTeaser } from "@/components/landing/CoachesTeaser";

const PILLARS = [
  "Churches",
  "Women & Children",
  "Veterans",
  "Law Enforcement",
  "Cancer Research",
  "Defense & Space Force",
  "Green Environment",
];

const Index = () => {
  const { user } = useAuth();

  return (
    <main
      className="relative min-h-screen bg-cover bg-center bg-fixed bg-no-repeat px-4 py-12 md:px-8"
      style={{ backgroundImage: `url(${landingBg})` }}
    >
      <Seo
        title="AGES — Adaptive Growth & Efficiency System"
        description="AGES finds your money & time leaks, builds your Millionaire Roadmap, and donates 5% of every subscription to one of 7 charity pillars."
        path="/"
      />
      <div className="absolute inset-0 z-0 bg-background/40" aria-hidden />
      <div className="relative z-10 mx-auto max-w-5xl">
        {/* Nav */}
        <nav className="mb-16 flex items-center justify-between">
          <AgesLogo size={32} />
          <Button asChild variant={user ? "default" : "outline"}>
            <Link to={user ? "/dashboard" : "/auth"}>
              {user ? "Open Dashboard" : "Sign in"}
            </Link>
          </Button>
        </nav>

        {/* Hero */}
        <section className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs text-gold">
            <HeartHandshake className="h-3.5 w-3.5" /> 5% of every subscription becomes charity
          </span>
          <h1 className="mt-6 text-4xl font-bold tracking-tight md:text-6xl">
            The <span className="text-gradient-wealth">Adaptive Growth</span>
            <br />
            & <span className="text-gradient-charity">Efficiency System</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Plug in your money and your health. AGES finds your leaks, builds your Millionaire
            Roadmap, and turns every dollar you earn into measurable impact.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="glow-wealth">
              <Link to="/auth">Start free</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/pricing">See plans · 5% to charity</Link>
            </Button>
          </div>

          {/* Hero illustration */}
          <div className="mt-12 flex justify-center">
            <div className="relative w-full max-w-2xl">
              <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-primary/20 via-gold/10 to-transparent blur-3xl" />
              <div className="relative overflow-hidden rounded-2xl border border-border/40 shadow-2xl">
                <img
                  src={agesHero}
                  srcSet={`${agesHero400} 400w, ${agesHero800} 800w, ${agesHero} 1024w`}
                  sizes="(max-width: 768px) 100vw, 672px"
                  alt="AGES — wealth, health, vitality, and design orbiting a brighter tomorrow"
                  width={1024}
                  height={1024}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full"
                />
                {/* Top tagline */}
                <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-background/80 via-background/40 to-transparent px-3 pb-8 pt-4 md:pt-6">
                  <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-[10px] font-semibold uppercase tracking-[0.15em] text-foreground sm:text-xs md:text-base md:tracking-[0.2em]">
                    <span className="text-gradient-wealth">Wealth</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-gradient-wealth">Health</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-gradient-wealth">Time</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-gradient-charity">Vitality</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-gradient-charity">Design</span>
                  </p>
                </div>
                {/* Bottom tagline */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent px-4 pb-4 pt-10 md:pb-6">
                  <p className="text-center text-sm italic text-foreground/90 md:text-base">
                    Analyzing today to create a better tomorrow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="mt-20">
          <h2 className="text-center text-sm uppercase tracking-widest text-muted-foreground">
            The 7 Pillars of Charity
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {PILLARS.map((p) => (
              <span
                key={p}
                className="rounded-full border border-gold/20 bg-gold/5 px-4 py-2 text-sm text-gold"
              >
                {p}
              </span>
            ))}
          </div>
        </section>

        {/* Value props */}
        <section className="mt-20 grid gap-6 md:grid-cols-3">
          <div className="rounded-xl border border-border/50 bg-card/60 p-6 backdrop-blur">
            <TrendingUp className="mb-3 h-6 w-6 text-primary" />
            <h3 className="font-semibold">Wealth Engine</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Plaid-powered leak detection and a personalized Millionaire Roadmap.
            </p>
          </div>
          <div className="rounded-xl border border-border/50 bg-card/60 p-6 backdrop-blur">
            <HeartHandshake className="mb-3 h-6 w-6 text-gold" />
            <h3 className="font-semibold">Pillar Pledge</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              $1 of every $19.99 subscription is auto-donated to the pillar you choose.
            </p>
          </div>
          <div className="rounded-xl border border-border/50 bg-card/60 p-6 backdrop-blur">
            <ShieldCheck className="mb-3 h-6 w-6 text-primary" />
            <h3 className="font-semibold">Earn Badges</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Bronze, silver, gold, platinum — your impact lives next to your name.
            </p>
          </div>
        </section>

        <StoriesCarousel />
        <CoachesTeaser />

        {/* Footer */}
        <footer className="mt-20 border-t border-border/30 pt-6 pb-8 text-center text-xs text-muted-foreground">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/refund" className="hover:text-foreground transition-colors">Refund Policy</Link>
          </div>
          <p className="mt-3">© {new Date().getFullYear()} AGES. All rights reserved.</p>
        </footer>
      </div>
    </main>
  );
};

export default Index;
